import axios from 'axios';
import { create } from 'zustand';
import { getLive } from './classroomApi';
import type { ClassSession, ClassActivity, ActivityResponseRecord, MyGroup } from './classroomApi';

// BP 22 — the live-activity poll shared by `/class` (LiveActivity) and the
// lecture-player Class panel (ClassPanel), and by the lecture player's header
// button (which only needs to know whether the CURRENT lesson has a live
// session, not render anything). Ref-counted start/stop so any number of
// mounted consumers share one poll loop instead of each opening its own —
// the header button and an open panel can both be watching at once.

const POLL_MS = 5000;

// 2026-09 rate-limit incident (see backend/src/server.js) — students sat on a
// stale "waiting for instructor" forever because a failing poll updated
// nothing visible, and a client that just retries a failing poll every 5s
// forever is exactly the traffic shape that caused the incident. A failed
// poll now backs off instead, and the UI gets a way to tell "nothing is live
// yet" (a normal, healthy response) apart from "we can't reach the server".
const MAX_BACKOFF_MS = 30_000;
// A 429 is the server explicitly asking us to slow down — start the backoff
// further out than an ordinary network hiccup, so recovering from a rate
// limit doesn't itself look like hammering it.
const RATE_LIMIT_BACKOFF_MS = 15_000;
// One blip (a dropped packet, a deploy restart) should not flip the page
// into "can't reach the server" — only two failures in a row (~10s of no
// contact, at the base poll rate) does.
const UNREACHABLE_AFTER_FAILURES = 2;

function backoffDelay(prevDelayMs: number, status: number | undefined): number {
  const doubled = prevDelayMs * 2;
  if (status === 429) return Math.min(Math.max(doubled, RATE_LIMIT_BACKOFF_MS), MAX_BACKOFF_MS);
  return Math.min(doubled, MAX_BACKOFF_MS);
}

interface ClassroomState {
  session: ClassSession | null;
  activity: ClassActivity | null;
  myResponses: ActivityResponseRecord[];
  /** design §6.6 — meeting-level; present even between activities. */
  myGroup: MyGroup | null;
  lastSeenActivityId: string | null;
  loading: boolean;
  /** Most recent poll failure's message (null once a poll succeeds). */
  error: string | null;
  /** True once poll failures land UNREACHABLE_AFTER_FAILURES times in a row
   *  — "we cannot reach the server". Never true just because a healthy poll
   *  found nothing live (that's session: null, error: null, unreachable:
   *  false — a normal, common state). The UI must tell these apart: showing
   *  a stale "waiting for instructor" with no sign the poll itself is
   *  failing is the exact failure the 2026-09 incident produced. */
  unreachable: boolean;
  watcherCount: number;
  intervalId: ReturnType<typeof setTimeout> | null;

  startWatching: () => void;
  stopWatching: () => void;
  refresh: () => Promise<void>;
  liveForLesson: (lessonId: string | null | undefined) => boolean;
  hasResponded: (stepKey: string) => boolean;
  /** WIP-012 — the student's own recorded response for a step, payload and
   * all, so ResponseControl can read back a prior `confidence` (or re-submit
   * the same answer payload with confidence added) without re-fetching. */
  myResponse: (stepKey: string) => ActivityResponseRecord | undefined;
}

// Poll bookkeeping that doesn't need to be reactive state: the current
// backoff delay, the consecutive-failure streak, and whether a poll loop is
// active. Module-scoped (there is exactly one poll loop, ref-counted across
// consumers, never one per store subscriber) and reset whenever watching
// restarts from zero. `loopActive` guards startWatching synchronously —
// unlike `intervalId` (only set once the in-flight refresh() resolves), it
// flips true immediately, so two consumers mounting back-to-back before the
// first refresh() returns don't each kick off their own loop.
let loopActive = false;
let failureStreak = 0;
let currentDelay = POLL_MS;

export const useClassroomStore = create<ClassroomState>((set, get) => ({
  session: null,
  activity: null,
  myResponses: [],
  myGroup: null,
  lastSeenActivityId: null,
  loading: false,
  error: null,
  unreachable: false,
  watcherCount: 0,
  intervalId: null,

  refresh: async () => {
    set({ loading: true });
    try {
      const res = await getLive();
      failureStreak = 0;
      currentDelay = POLL_MS;
      set((s) => ({
        session: res.session,
        activity: res.activity,
        myResponses: res.my_responses || [],
        myGroup: res.my_group || null,
        lastSeenActivityId: res.activity ? res.activity.id : s.lastSeenActivityId,
        loading: false,
        error: null,
        unreachable: false,
      }));
    } catch (err) {
      failureStreak += 1;
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      currentDelay = backoffDelay(currentDelay, status);
      set({
        loading: false,
        error: status === 429
          ? 'The class page is asking us to slow down — retrying shortly.'
          : 'Could not reach the class page. Retrying…',
        unreachable: failureStreak >= UNREACHABLE_AFTER_FAILURES,
      });
    }
    // Reschedule only while still watched and the loop hasn't been stopped —
    // stopWatching may have run while this refresh was in flight, and wins.
    if (loopActive && get().watcherCount > 0) {
      const id = setTimeout(() => { get().refresh(); }, currentDelay);
      set({ intervalId: id });
    }
  },

  startWatching: () => {
    const { watcherCount } = get();
    set({ watcherCount: watcherCount + 1 });
    if (!loopActive) {
      loopActive = true;
      failureStreak = 0;
      currentDelay = POLL_MS;
      get().refresh(); // kicks off the loop; refresh() reschedules itself
    }
  },

  stopWatching: () => {
    const { watcherCount, intervalId } = get();
    const next = Math.max(0, watcherCount - 1);
    set({ watcherCount: next });
    if (next === 0) {
      loopActive = false;
      if (intervalId) {
        clearTimeout(intervalId);
        set({ intervalId: null });
      }
    }
  },

  liveForLesson: (lessonId) => {
    if (!lessonId) return false;
    const { session } = get();
    return !!session && session.status === 'live' && session.lesson_id === lessonId;
  },

  hasResponded: (stepKey) => {
    return get().myResponses.some((r) => r.step_key === stepKey);
  },

  // ── live-activity set selectors (design §9, build order §2.5) — one line
  // per issue. Each adds its own selector; nothing above this line moves.
  myResponse: (stepKey) => get().myResponses.find((r) => r.step_key === stepKey),
}));
